import React from 'react';
import {
  Card, Row, Col, Statistic, Typography, Tabs, List, Avatar, Tag
} from 'antd';
import {
  FundOutlined,
  BarChartOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const { Text } = Typography;
const { TabPane } = Tabs;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Mock data for the 4 MF holdings
const FUNDS = [
  {
    name: 'Quant ELSS Tax Save',
    shortName: 'Quant ELSS',
    category: 'ELSS',
    nav: 422.67,
    units: 372.23,
    invested: 100000,
    color: '#0088FE',
    returns: { oneYear: 28.4, threeYear: 21.6, fiveYear: 18.9 },
    benchmarkReturns: { oneYear: 18.2, threeYear: 14.5, fiveYear: 13.1 },
    risk: { volatility: 18.2, sharpe: 1.45, beta: 1.12, maxDrawdown: -14.3 },
    navTrend: [
      { month: 'Dec', nav: 310 }, { month: 'Jan', nav: 335 }, { month: 'Feb', nav: 355 },
      { month: 'Mar', nav: 342 }, { month: 'Apr', nav: 388 }, { month: 'May', nav: 423 },
    ],
  },
  {
    name: 'Parag Parikh Flexi Cap',
    shortName: 'PP Flexi Cap',
    category: 'Multi Cap',
    nav: 78.65,
    units: 1899.31,
    invested: 120000,
    color: '#00C49F',
    returns: { oneYear: 22.1, threeYear: 17.8, fiveYear: 16.4 },
    benchmarkReturns: { oneYear: 18.2, threeYear: 14.5, fiveYear: 13.1 },
    risk: { volatility: 14.8, sharpe: 1.28, beta: 0.88, maxDrawdown: -11.2 },
    navTrend: [
      { month: 'Dec', nav: 58 }, { month: 'Jan', nav: 62 }, { month: 'Feb', nav: 65 },
      { month: 'Mar', nav: 63 }, { month: 'Apr', nav: 72 }, { month: 'May', nav: 79 },
    ],
  },
  {
    name: 'Quant Small Cap',
    shortName: 'Quant Small Cap',
    category: 'Small Cap',
    nav: 271.1,
    units: 204.79,
    invested: 42000,
    color: '#FFBB28',
    returns: { oneYear: 35.6, threeYear: 28.9, fiveYear: 24.1 },
    benchmarkReturns: { oneYear: 24.3, threeYear: 18.7, fiveYear: 16.2 },
    risk: { volatility: 24.5, sharpe: 1.55, beta: 1.38, maxDrawdown: -19.8 },
    navTrend: [
      { month: 'Dec', nav: 190 }, { month: 'Jan', nav: 212 }, { month: 'Feb', nav: 228 },
      { month: 'Mar', nav: 220 }, { month: 'Apr', nav: 255 }, { month: 'May', nav: 271 },
    ],
  },
  {
    name: 'Canara Robeco Small Cap',
    shortName: 'CR Small Cap',
    category: 'Small Cap',
    nav: 39.26,
    units: 340.03,
    invested: 10000,
    color: '#FF8042',
    returns: { oneYear: 29.8, threeYear: 22.4, fiveYear: 19.7 },
    benchmarkReturns: { oneYear: 24.3, threeYear: 18.7, fiveYear: 16.2 },
    risk: { volatility: 22.1, sharpe: 1.38, beta: 1.25, maxDrawdown: -16.5 },
    navTrend: [
      { month: 'Dec', nav: 28 }, { month: 'Jan', nav: 30 }, { month: 'Feb', nav: 32 },
      { month: 'Mar', nav: 31 }, { month: 'Apr', nav: 36 }, { month: 'May', nav: 39 },
    ],
  },
];

const totalInvested = FUNDS.reduce((s, f) => s + f.invested, 0);
const totalCurrent = FUNDS.reduce((s, f) => s + Math.round(f.nav * f.units * 100) / 100, 0);
const totalPnL = totalCurrent - totalInvested;
const totalPnLPct = (totalPnL / totalInvested) * 100;

const fundAllocationData = FUNDS.map(f => ({
  name: f.shortName,
  value: Math.round(f.nav * f.units),
}));

// Weighted sector allocation: Small Cap (59.25%) + ELSS (40.75%)
const sectorAllocationData = [
  { sector: 'Finance', value: 10.28 },
  { sector: 'Healthcare', value: 9.35 },
  { sector: 'Capital Goods', value: 7.96 },
  { sector: 'Bank', value: 7.83 },
  { sector: 'Automobile & Ancillaries', value: 6.33 },
  { sector: 'FMCG', value: 5.91 },
  { sector: 'IT', value: 4.64 },
  { sector: 'Others', value: 4.44 },
  { sector: 'Construction Materials', value: 4.21 },
  { sector: 'Power', value: 3.39 },
  { sector: 'Electricals', value: 2.71 },
  { sector: 'Retailing', value: 2.53 },
].sort((a, b) => b.value - a.value);

const marketCapData = [
  { name: 'Large Cap', value: 34.94, portfolioValue: 72986.57 },
  { name: 'Mid Cap', value: 14.76, portfolioValue: 30834.57 },
  { name: 'Small Cap', value: 45.85, portfolioValue: 95783.42 },
  { name: 'Other', value: 4.44, portfolioValue: 9281.58 },
];

const returnsComparisonData = FUNDS.map(f => ({
  name: f.shortName,
  '1Y Return': f.returns.oneYear,
  '3Y Return': f.returns.threeYear,
  '5Y Return': f.returns.fiveYear,
  'Benchmark 1Y': f.benchmarkReturns.oneYear,
}));


const MutualFundAnalytics: React.FC = () => {
  const renderPortfolioOverview = () => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card title="Portfolio Summary">
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8}>
              <Statistic
                title="Current Value"
                value={totalCurrent}
                precision={0}
                prefix="₹"
              />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic
                title="Total Invested"
                value={totalInvested}
                precision={0}
                prefix="₹"
              />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic
                title="Total P&L"
                value={totalPnL}
                precision={0}
                prefix="₹"
                valueStyle={{ color: totalPnL >= 0 ? '#3f8600' : '#cf1322' }}
                suffix={
                  <Text style={{ fontSize: 14, color: totalPnL >= 0 ? '#3f8600' : '#cf1322' }}>
                    {` (${totalPnLPct.toFixed(1)}%)`}
                  </Text>
                }
              />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic
                title="Absolute Return"
                value={totalPnLPct}
                precision={1}
                suffix="%"
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic
                title="No. of Funds"
                value={FUNDS.length}
              />
            </Col>
          </Row>
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card title="Fund-wise Allocation">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={fundAllocationData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {fundAllocationData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(v: number) => `₹${v.toLocaleString()}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card title="Market Cap Breakup">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={marketCapData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, value }) => `${name} ${value}%`}
              >
                {marketCapData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(v: number, name: string, props: any) => [`₹${props.payload.portfolioValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })} (${v}%)`, name]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </Col>
      <Col span={24}>
        <Card title="Sector Allocation">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={sectorAllocationData}
              margin={{ top: 5, right: 20, left: 0, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="sector" tick={{ fontSize: 11, angle: -45, textAnchor: 'end' } as any} interval={0} />
              <YAxis tickFormatter={(v) => `${v}%`} />
              <RechartsTooltip formatter={(v: number) => `${v}%`} />
              <Bar dataKey="value" fill="#0088FE" name="Allocation" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>
    </Row>
  );

  const renderPerformance = () => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card title="Fund Returns vs Benchmark (%)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={returnsComparisonData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `${v}%`} />
              <RechartsTooltip formatter={(v: number) => `${v}%`} />
              <Legend />
              <Bar dataKey="1Y Return" fill="#0088FE" />
              <Bar dataKey="3Y Return" fill="#00C49F" />
              <Bar dataKey="5Y Return" fill="#FFBB28" />
              <Bar dataKey="Benchmark 1Y" fill="#FF8042" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Col>

      <Col span={24}>
        <Card title="Fund-wise P&L">
          <List
            dataSource={FUNDS}
            renderItem={(fund) => {
              const currentVal = Math.round(fund.nav * fund.units * 100) / 100;
              const pnl = currentVal - fund.invested;
              const pnlPct = (pnl / fund.invested) * 100;
              return (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar style={{ backgroundColor: fund.color }}>
                        <FundOutlined />
                      </Avatar>
                    }
                    title={fund.name}
                    description={
                      <Tag color={fund.category === 'ELSS' ? 'purple' : fund.category === 'Multi Cap' ? 'blue' : 'orange'}>
                        {fund.category}
                      </Tag>
                    }
                  />
                  <div style={{ textAlign: 'right' }}>
                    <div>
                      <Text strong>₹{currentVal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Text>
                    </div>
                    <div>
                      <Text style={{ color: pnl >= 0 ? '#3f8600' : '#cf1322' }}>
                        {pnl >= 0 ? '+' : ''}₹{Math.abs(pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        {' '}({pnlPct.toFixed(1)}%)
                      </Text>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        </Card>
      </Col>
    </Row>
  );


  return (
    <div>
      <Tabs defaultActiveKey="overview">
        <TabPane
          tab={<span><LineChartOutlined /> Overview</span>}
          key="overview"
        >
          {renderPortfolioOverview()}
        </TabPane>
        <TabPane
          tab={<span><BarChartOutlined /> Performance</span>}
          key="performance"
        >
          {renderPerformance()}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default MutualFundAnalytics;
